// Libs
import classNames from 'classnames/bind';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid2, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
// Components
import { BaseInput, BaseSelect, BaseLabel, Loading, BaseDatePicker, BaseDynamicSelect } from '~/components/common';
// Others
import { useAppDispatch } from '~/utils/redux/hooks';
import { schema, defaultValuesTaskForm } from './helper';
import { IBaseOption, IBodyUpdate, IPayloadCallApiFn, IResultCallApiFn } from '~/utils/interface/common';
import { cleanEmptyFields, createFormData, getChangedDataForm, getFullName } from '~/utils/helper/common';
import {
  DEFAULT_LIMIT_PAGE,
  DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA,
  optionTaskPriority,
  optionTaskStatus,
  optionTaskTag,
} from '~/utils/constants/common';
import { IFormTask } from '~/utils/interface/task';
import { taskActions } from '~/thunks/task/taskSlice';
import { getListUser } from '~/thunks/user/userThunk';
import { IQueryParamUser } from '~/utils/interface/user';
import { BusinessTypeEnum } from '~/utils/enum/common';
import { createTask, getDetailTask, updateTask } from '~/thunks/task/taskThunk';
// Styles, images, icons
import styles from './TaskForm.module.scss';

type Props = {
  taskId?: string;
  businessType?: BusinessTypeEnum;
  onClose: () => void;
};

const cx = classNames.bind(styles);

const TaskForm = (props: Props) => {
  //#region Destructuring Props
  const { taskId, businessType, onClose } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/client', 'common/base']);
  const { id: businessId } = useParams();
  const dispatch = useAppDispatch();
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, dirtyFields },
  } = useForm<IFormTask>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesTaskForm,
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState(false);
  const [assigneeNameDefault, setAssigneeNameDefault] = useState<string[]>([]);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook

  useEffect(() => {
    if (!taskId) return;

    handleGetDetailTask(taskId);
  }, [taskId]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetDetailTask = (taskId: string) => {
    setIsLoading(true);

    dispatch(getDetailTask(taskId))
      .unwrap()
      .then((res) => {
        const { data } = res;

        if (!data) return;

        const { id, ...rest } = data;

        const newData: IFormTask = {
          ...rest,
          ...(businessId && { businessId: businessId }),
          ...(businessType && { businessType: businessType }),
          assigneeIds: data.assignees
            ? data?.assignees?.map((item) => item.id).filter((id): id is string => id !== undefined)
            : undefined,
          tags: data.tags ? data.tags.map((item) => item) : undefined,
        };

        setAssigneeNameDefault(
          data?.assignees?.map((item) => getFullName({ firstName: item?.firstName, lastName: item?.lastName }))
        );
        reset(cleanEmptyFields(newData));
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const handleCreateTask = async (data: IFormTask) => {
    const cleanData = cleanEmptyFields({
      ...data,
      ...(businessId && { businessId: businessId }),
      ...(businessType && { businessType: businessType }),
      assigneeIds: (data?.assigneeIds || [])?.length > 0 ? data?.assigneeIds : undefined,
    });

    const formData = await createFormData(cleanData, ['taskFile']);

    setIsLoading(true);

    dispatch(createTask(formData))
      .unwrap()
      .then(() => {
        dispatch(taskActions.setRefreshTaskList(true));
        handleCloseModal();
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const handleUpdateTask = (data: IFormTask) => {
    if (!taskId) return;

    const cleanData = getChangedDataForm(dirtyFields, data) as IFormTask;

    const payload: IBodyUpdate<IFormTask> = {
      id: taskId,
      body: cleanData,
    };

    setIsLoading(true);
    dispatch(updateTask(payload))
      .unwrap()
      .then(() => {
        dispatch(taskActions.setRefreshTaskList(true));
        handleCloseModal();
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const fetchOptionsStatus = async (): Promise<IResultCallApiFn<IBaseOption>> => ({
    optionsResp: optionTaskStatus,
    hasMoreResp: false,
  });

  const fetchOptionsPriority = async (): Promise<IResultCallApiFn<IBaseOption>> => ({
    optionsResp: optionTaskPriority,
    hasMoreResp: false,
  });

  const fetchOptionsTags = async (): Promise<IResultCallApiFn<IBaseOption>> => ({
    optionsResp: optionTaskTag,
    hasMoreResp: false,
  });

  const fetchOptionAssignee = useCallback(
    async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IBaseOption>> => {
      const { page, searchKey } = payload;

      const payloadParams: IQueryParamUser = {
        limit: DEFAULT_LIMIT_PAGE,
        page: page,
        ...(searchKey && { textSearch: searchKey }),
      };

      try {
        const resp = await dispatch(getListUser(payloadParams)).unwrap();

        const { responses, pagination } = resp?.data;

        const formatOptions: IBaseOption[] = responses.map((item) => ({
          value: item?.id,
          label: getFullName({ firstName: item?.firstName, lastName: item?.lastName }),
        }));

        const hasMoreResp: boolean = pagination.page < pagination.totalPages || false;

        return {
          optionsResp: formatOptions,
          hasMoreResp: hasMoreResp,
        };
      } catch (_error) {
        return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
      }
    },
    []
  );

  const handleCloseModal = () => {
    onClose?.();
    reset();
  };
  //#endregion Handle Function

  return (
    <Dialog
      id='taskFormComponent'
      aria-labelledby='taskForm'
      open={true}
      onClose={handleCloseModal}
      fullWidth
      maxWidth='md'
    >
      <DialogTitle id='taskForm' className={cx('header')}>
        <Stack direction='column' spacing={1}>
          <BaseLabel label={t(!taskId ? 'form_task_title' : 'form_task_title_edit')} styleType='bigTitle' />
        </Stack>
      </DialogTitle>

      <DialogContent className={cx('content')} dividers>
        <Grid2 container spacing={3}>
          <Grid2 size={12}>
            <Controller
              control={control}
              name='title'
              render={({ field }) => (
                <BaseInput
                  required
                  label={t('form_task_title_label')}
                  value={field.value}
                  onChange={field.onChange}
                  errorMessage={errors.title?.message}
                />
              )}
            />
          </Grid2>

          <Grid2 size={12}>
            <Controller
              control={control}
              name='description'
              render={({ field }) => (
                <BaseInput
                  multiline
                  height={'auto'}
                  rows={6}
                  label={t('form_task_note_label')}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name='assigneeIds'
              render={({ field: { value, onChange } }) => (
                <BaseDynamicSelect
                  isSearch
                  multiple={true}
                  label={t('form_task_assignee_label')}
                  placeholder={t('form_task_assignee_placeholder')}
                  callApiFn={fetchOptionAssignee}
                  value={value}
                  onChange={(selected) => {
                    onChange(selected.idSelected);
                  }}
                  itemNameDefault={assigneeNameDefault}
                />
              )}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name='dueDate'
              render={({ field: { value, onChange } }) => (
                <BaseDatePicker
                  required
                  height={40}
                  mode='date'
                  label={t('form_task_due_date_label')}
                  value={value}
                  onChange={onChange}
                />
              )}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name='status'
              render={({ field }) => (
                <BaseSelect
                  styleType='contained'
                  gap={8}
                  label={t('form_task_status_label')}
                  placeholder={t('form_task_status_placeholder')}
                  callApiFn={fetchOptionsStatus}
                  value={field.value}
                  onChange={(opt) => field.onChange(opt?.value)}
                />
              )}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name='priority'
              render={({ field }) => (
                <BaseSelect
                  styleType='contained'
                  gap={8}
                  label={t('form_task_priority_label')}
                  placeholder={t('form_task_priority_placeholder')}
                  callApiFn={fetchOptionsPriority}
                  value={field.value}
                  onChange={(opt) => field.onChange(opt?.value)}
                />
              )}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name='tags'
              render={({ field: { value, onChange } }) => (
                <BaseDynamicSelect
                  gap={8}
                  multiple={true}
                  label={t('form_task_tags_label')}
                  placeholder={t('form_task_tags_placeholder')}
                  callApiFn={fetchOptionsTags}
                  value={value}
                  onChange={(selected) => {
                    onChange(selected.idSelected);
                  }}
                />
              )}
            />
          </Grid2>
        </Grid2>
      </DialogContent>

      <DialogActions className={cx('groupButton')}>
        <Button variant='outlined' className={cx('buttonCancel')} onClick={handleCloseModal}>
          {t('common_action_cancel', { ns: 'common/base' })}
        </Button>

        <Button
          variant='contained'
          className={cx('buttonSave')}
          disabled={taskId ? Object.keys(dirtyFields).length === 0 : false}
          onClick={handleSubmit(!taskId ? handleCreateTask : handleUpdateTask)}
        >
          {t('common_action_save', { ns: 'common/base' })}
        </Button>
      </DialogActions>

      {isLoading && <Loading />}
    </Dialog>
  );
};

export default TaskForm;
